import { useEffect, useState, useCallback } from "react";
import {
  Droplets, Users2, PhoneOff, Percent, Clock, AlarmClock, CalendarX,
  Loader2, Search, ArrowUpDown, ChevronLeft, ChevronRight, X,
} from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip as RTooltip,
  ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";
import api, { apiError } from "@/lib/api";
import { minDelay } from "@/lib/ux";
import { Loading, ErrorView, EmptyState } from "@/components/StateViews";
import { useAuth } from "@/contexts/AuthContext";
import DateRangePicker from "@/components/DateRangePicker";

const BAR = "#2F6BFF";
const STATUS_COLORS = { "Not Contacted": "#ef4444", Contacted: "#10b981", Qualified: "#2F6BFF", Junk: "#a1a1aa", Lost: "#f59e0b" };

const fmtMins = (m) => {
  if (m == null) return "—";
  if (m < 60) return `${m}m`;
  if (m < 1440) return `${Math.round(m / 60)}h`;
  return `${Math.round(m / 1440)}d`;
};
const fmtAge = (h) => (h == null ? "—" : h < 24 ? `${h}h` : `${Math.round(h / 24)}d`);
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "—");

const QUICK = [
  ["", "All"], ["not_contacted", "Not contacted"], ["h24", "24h+"],
  ["h48", "48h+"], ["h72", "72h+"], ["no_next", "No next activity"],
];

function ChartCard({ title, children, empty }) {
  return (
    <div className="rounded-2xl border border-sky/70 bg-white p-4" data-testid={`chart-${title.replace(/\s+/g, "-").toLowerCase()}`}>
      <h3 className="mb-3 font-sora text-sm font-semibold text-navy">{title}</h3>
      {empty ? <div className="flex h-48 items-center justify-center text-xs text-ink/40">No data for the selected filters.</div> : <div className="h-48">{children}</div>}
    </div>
  );
}

export default function SalesLeakageReport() {
  const { options } = useAuth();
  const [filters, setFilters] = useState({ created_from: "", created_to: "", salesperson: "", source: "", campaign: "", stage: "", quality: "", service: "" });
  const [opts, setOpts] = useState({ salespersons: [], sources: [], campaigns: [], services: [] });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // table state
  const [rows, setRows] = useState(null);
  const [tmeta, setTmeta] = useState({ total: 0, page: 1, pages: 1 });
  const [tloading, setTloading] = useState(false);
  const [quick, setQuick] = useState("");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("age_desc");
  const [page, setPage] = useState(1);

  const cleanParams = () => Object.fromEntries(Object.entries(filters).filter(([, v]) => v));

  useEffect(() => {
    api.get("/reports/filter-options").then(({ data }) => setOpts(data.data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const { data } = await minDelay(api.get("/reports/leakage", { params: cleanParams() }));
      setData(data.data);
    } catch (e) { setError(apiError(e)); }
    finally { setLoading(false); }
  }, [filters]); // eslint-disable-line

  const loadTable = useCallback(async () => {
    setTloading(true);
    try {
      const params = { ...cleanParams(), quick, q, sort, page, page_size: 25 };
      const { data } = await api.get("/reports/leakage/table", { params });
      setRows(data.data.rows);
      setTmeta({ total: data.data.total, page: data.data.page, pages: data.data.pages });
    } catch (e) { setRows([]); }
    finally { setTloading(false); }
  }, [filters, quick, q, sort, page]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadTable(); }, [loadTable]);
  useEffect(() => { setPage(1); }, [filters, quick, q, sort]);

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const clearFilters = () => { setFilters({ created_from: "", created_to: "", salesperson: "", source: "", campaign: "", stage: "", quality: "", service: "" }); setQuick(""); setQ(""); };
  const hasFilters = Object.values(filters).some(Boolean) || quick || q;

  if (loading && !data) return <Loading label="Loading leakage report..." />;
  if (error) return <ErrorView message={error} onRetry={load} />;

  const k = data.kpis;
  const c = data.charts;
  const spSelected = !!filters.salesperson;
  const cards = [
    { label: "Total New Leads", value: k.total_leads, icon: Users2, tone: "text-loop", bg: "bg-sky/50" },
    { label: "Not Contacted", value: k.not_contacted, icon: PhoneOff, tone: "text-red-500", bg: "bg-red-50" },
    { label: "Not Contacted %", value: `${k.not_contacted_pct}%`, icon: Percent, tone: "text-red-500", bg: "bg-red-50" },
    { label: "Not Contacted 24h+", value: k.nc_24h, icon: Clock, tone: "text-amber-500", bg: "bg-amber-50" },
    { label: "Not Contacted 48h+", value: k.nc_48h, icon: AlarmClock, tone: "text-orange-500", bg: "bg-orange-50" },
    { label: "Not Contacted 72h+", value: k.nc_72h, icon: AlarmClock, tone: "text-red-600", bg: "bg-red-50" },
    { label: "Avg First Response", value: fmtMins(k.avg_first_response_min), icon: Clock, tone: "text-navy", bg: "bg-mist" },
    { label: "No Next Activity", value: k.no_next_activity, icon: CalendarX, tone: "text-purple-500", bg: "bg-purple-50" },
  ];

  const ncEmpty = c.contact_status.every((x) => !x.value);

  return (
    <div className="space-y-6" data-testid="leakage-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-sora text-3xl font-bold text-navy"><Droplets className="h-7 w-7 text-loop" />Sales Leakage</h1>
          <p className="flex items-center gap-2 text-sm text-ink/60">
            Where leads leak — focused on the Not Contacted stage.
            {loading && data && <span className="flex items-center gap-1 text-loop"><Loader2 className="h-3.5 w-3.5 animate-spin" />Refreshing…</span>}
          </p>
        </div>
      </div>

      {/* Global filters */}
      <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-sky/70 bg-white p-3" data-testid="leakage-filters">
        <DateRangePicker from={filters.created_from} to={filters.created_to} onChange={({ created_from, created_to }) => setFilters((f) => ({ ...f, created_from, created_to }))} testid="leakage-date" />
        <select value={filters.salesperson} onChange={(e) => setF("salesperson", e.target.value)} data-testid="f-salesperson" className="h-9 rounded-lg border border-sky px-2 text-xs outline-none focus:border-loop">
          <option value="">All salespersons</option>
          {opts.salespersons.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select value={filters.source} onChange={(e) => setF("source", e.target.value)} data-testid="f-source" className="h-9 rounded-lg border border-sky px-2 text-xs outline-none focus:border-loop">
          <option value="">All sources</option>
          {opts.sources.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filters.campaign} onChange={(e) => setF("campaign", e.target.value)} data-testid="f-campaign" className="h-9 rounded-lg border border-sky px-2 text-xs outline-none focus:border-loop">
          <option value="">All campaigns</option>
          {opts.campaigns.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filters.stage} onChange={(e) => setF("stage", e.target.value)} data-testid="f-stage" className="h-9 rounded-lg border border-sky px-2 text-xs outline-none focus:border-loop">
          <option value="">All stages</option>
          {(options?.stages || []).map((s) => <option key={s} value={s}>{options?.stage_meta?.[s]?.name || s}</option>)}
        </select>
        <select value={filters.quality} onChange={(e) => setF("quality", e.target.value)} data-testid="f-quality" className="h-9 rounded-lg border border-sky px-2 text-xs outline-none focus:border-loop">
          <option value="">All quality</option>
          {(options?.qualities || []).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filters.service} onChange={(e) => setF("service", e.target.value)} data-testid="f-service" className="h-9 rounded-lg border border-sky px-2 text-xs outline-none focus:border-loop">
          <option value="">All services</option>
          {opts.services.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {hasFilters && <button onClick={clearFilters} data-testid="clear-filters" className="flex items-center gap-1 rounded-lg border border-sky px-2.5 py-1.5 text-xs font-medium text-ink/60 hover:bg-mist"><X className="h-3.5 w-3.5" />Clear</button>}
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="leakage-kpis">
        {cards.map((t) => (
          <div key={t.label} className="rounded-2xl border border-sky/70 bg-white p-4">
            <div className={`mb-2 inline-flex rounded-lg p-2 ${t.bg}`}><t.icon className={`h-4 w-4 ${t.tone}`} /></div>
            <p className="font-sora text-2xl font-bold text-navy">{t.value}</p>
            <p className="text-xs text-ink/55">{t.label}</p>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard title="Contact Status Breakdown" empty={ncEmpty}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={c.contact_status} margin={{ top: 5, right: 10, bottom: 5, left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e8eef7" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false} />
              <RTooltip />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {c.contact_status.map((e) => <Cell key={e.name} fill={STATUS_COLORS[e.name] || BAR} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={spSelected ? "Not Contacted Aging (selected salesperson)" : "Not Contacted by Salesperson"} empty={(spSelected ? c.nc_aging_buckets : c.nc_by_salesperson).every((x) => !x.value)}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={spSelected ? c.nc_aging_buckets : c.nc_by_salesperson} margin={{ top: 5, right: 10, bottom: 5, left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e8eef7" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748b" }} interval={0} angle={spSelected ? 0 : -20} textAnchor={spSelected ? "middle" : "end"} height={spSelected ? 30 : 50} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false} />
              <RTooltip />
              <Bar dataKey="value" fill="#ef4444" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="First Response Aging Buckets" empty={c.response_buckets.every((x) => !x.value)}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={c.response_buckets} margin={{ top: 5, right: 10, bottom: 5, left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e8eef7" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#64748b" }} interval={0} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false} />
              <RTooltip />
              <Bar dataKey="value" fill={BAR} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Not Contacted Trend" empty={!c.trend.length}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={c.trend} margin={{ top: 5, right: 10, bottom: 5, left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e8eef7" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 9, fill: "#64748b" }} tickFormatter={fmtDate} minTickGap={20} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} allowDecimals={false} />
              <RTooltip />
              <Line type="monotone" dataKey="total" stroke="#94a3b8" strokeWidth={2} dot={false} name="Total" />
              <Line type="monotone" dataKey="nc" stroke="#ef4444" strokeWidth={2} dot={false} name="Not contacted" />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Detailed table */}
      <div className="rounded-2xl border border-sky/70 bg-white" data-testid="leakage-table">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-sky/60 p-3">
          <div className="flex flex-wrap items-center gap-1.5">
            {QUICK.map(([v, l]) => (
              <button key={v} onClick={() => setQuick(v)} data-testid={`quick-${v || "all"}`} className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors ${quick === v ? "bg-loop text-white" : "border border-sky text-ink/60 hover:bg-mist"}`}>{l}</button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink/40" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / phone…" data-testid="table-search" className="h-9 w-52 rounded-lg border border-sky pl-8 pr-2 text-xs outline-none focus:border-loop" />
            </div>
            <button onClick={() => setSort((s) => (s === "age_desc" ? "age_asc" : "age_desc"))} data-testid="sort-age" className="flex items-center gap-1 rounded-lg border border-sky px-2.5 py-1.5 text-xs font-medium text-ink/60 hover:bg-mist"><ArrowUpDown className="h-3.5 w-3.5" />Age</button>
          </div>
        </div>

        {tloading && !rows ? (
          <div className="flex items-center justify-center py-12 text-sm text-ink/50"><Loader2 className="h-4 w-4 animate-spin" /></div>
        ) : !rows?.length ? (
          <EmptyState title="No report data found for the selected filters." />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-mist/70 text-left text-ink/50">
                <tr>
                  {["Lead", "Phone", "Company", "Source", "Campaign", "Created", "Salesperson", "Stage", "Quality", "Service", "Age", "1st done?", "Next", "NC bucket"].map((h) => <th key={h} className="whitespace-nowrap px-3 py-2 font-semibold">{h}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-sky/40">
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-sky/10" data-testid={`row-${r.id}`}>
                    <td className="whitespace-nowrap px-3 py-2 font-medium text-navy">{r.name}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-ink/70">{r.phone}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-ink/70">{r.business_name}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-ink/70">{r.source}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-ink/70">{r.campaign}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-ink/60">{fmtDate(r.created_at)}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-ink/70">{r.owner}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-ink/70">{r.stage}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-ink/70">{r.quality}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-ink/60">{r.service}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-ink/70">{fmtAge(r.age_hours)}</td>
                    <td className="whitespace-nowrap px-3 py-2">{r.first_activity_done ? <span className="text-emerald-600">Yes</span> : <span className="text-red-500">No</span>}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-ink/60">{r.next_activity ? fmtDate(r.next_activity) : <span className="text-amber-600">None</span>}</td>
                    <td className="whitespace-nowrap px-3 py-2">{r.nc_bucket !== "—" ? <span className="rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-semibold text-red-600">{r.nc_bucket}</span> : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {rows?.length > 0 && (
          <div className="flex items-center justify-between border-t border-sky/60 p-3 text-xs text-ink/60">
            <span>{tmeta.total} leads · page {tmeta.page} of {tmeta.pages}</span>
            <div className="flex items-center gap-1">
              <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="page-prev" className="rounded-lg border border-sky p-1.5 disabled:opacity-40 hover:bg-mist"><ChevronLeft className="h-4 w-4" /></button>
              <button disabled={page >= tmeta.pages} onClick={() => setPage((p) => p + 1)} data-testid="page-next" className="rounded-lg border border-sky p-1.5 disabled:opacity-40 hover:bg-mist"><ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
